import React, { useRef, useCallback } from 'react';
import { Box } from 'roo-ui/components';
import { useSelector } from 'react-redux';
import PageBlock from 'components/PageBlock';
import PropertyHelmet from './PropertyHelmet';
import PropertySummary from './PropertySummary';
import PropertyDescription from './PropertyDescription';
import PropertyFacilities from './PropertyFacilities';
import PropertyKnowBeforeYouGo from './PropertyKnowBeforeYouGo';
import PaymentOptions from './PaymentOptions';
import SearchControls from './SearchControls';
import LegacyPropertyAvailability from './LegacyPropertyAvailability';
import PropertyAvailability from './PropertyAvailability';
import PropertyAvailabilitySummary from './PropertyAvailabilitySummary';
import BackToSearchLink from './BackToSearchLink';
import NoQueryMessage from './NoQueryMessage';
import { getProperty, getTripAdvisorRating } from 'store/property/propertySelectors';
import { getHasValidQuery } from 'store/propertyAvailability/propertyAvailabilitySelectors';
import PropertyMap from 'components/PropertyMap';
import CampaignMessaging from 'components/CampaignMessaging';
import WelcomeMessage from 'components/WelcomeMessage';
import DynamicMessageBox from 'pages/ListSearch/Messaging/DynamicMessageBox';
import MemberFavouritesFetcher from './MemberFavouritesFetcher';
import Waypoints from 'components/Waypoints';
import JumpToMenu from './JumpToMenu';
import RoomTypeFilters from './RoomTypeFilters';
import TreatmentToggle from 'components/TreatmentToggle';
import LegacyBrowserBoundary from 'components/LegacyBrowserBoundary';
import TimeOnPageReporter from 'components/TimeOnPageReporter';
import RecommendedProperties from 'pages/Property/RecommendedProperties';

const PropertyLayout = React.memo(() => {
  const hasQuery = useSelector(getHasValidQuery);
  const property = useSelector(getProperty);
  const tripAdvisorRating = useSelector(getTripAdvisorRating);

  const menuItems = [
    { name: 'photos', text: 'Photos', id: 'photos', ref: useRef() },
    { name: 'rooms', text: 'Rooms', id: 'rooms', ref: useRef() },
    { name: 'about', text: 'About this property', id: 'about-property', ref: useRef() },
    { name: 'location', text: 'Location', id: 'location', ref: useRef() },
    { name: 'policies', text: 'Property policies', id: 'property-policies', ref: useRef() },
  ];

  const getMenuRef = useCallback((name) => menuItems.find((item) => item.name === name).ref, [menuItems]);

  if (!property) return null;
  const hasPropertyFacilities = property.propertyFacilities && property.propertyFacilities.length > 0;

  return (
    <Box position="relative">
      <Waypoints />
      <PropertyHelmet property={property} />
      <MemberFavouritesFetcher />
      <LegacyBrowserBoundary>
        <TimeOnPageReporter eventName="view_property_duration" />
      </LegacyBrowserBoundary>

      <PageBlock bg="white" pt={6} pb={4} borderBottom={1} borderColor="greys.porcelain" printable={false}>
        <BackToSearchLink />
      </PageBlock>

      <JumpToMenu menuItems={menuItems} />

      <PageBlock bg="white" gutter={[0, 3]} ref={getMenuRef('photos')}>
        <div id="photos"></div>
        <CampaignMessaging showDefaultMessage={false} pt={[0, 6]} />
        <PropertySummary property={property} tripAdvisorRating={tripAdvisorRating} hasPropertyFacilities={hasPropertyFacilities} />
      </PageBlock>

      <PageBlock pt={[0, 5]} bg="white" display="flex">
        <DynamicMessageBox />
      </PageBlock>

      <PageBlock bg="white" gutter={0}>
        <WelcomeMessage />
      </PageBlock>

      <Box ref={getMenuRef('rooms')}>
        <div id="rooms"></div>
        <PageBlock bg="white" pt={[0, 6, 6]} pb={10} printable={false}>
          <PropertyAvailabilitySummary propertyName={property.name} />
          <NoQueryMessage hasQuery={hasQuery} propertyName={property.name} />
          <SearchControls />
        </PageBlock>

        <PaymentOptions />
        <TreatmentToggle split="property_page_filters" treatment="on">
          <RoomTypeFilters />
        </TreatmentToggle>

        <PageBlock py={[7, 10]} overflowY={['hidden', 'inherit']}>
          <TreatmentToggle split="property_page_availability" treatment="on">
            <PropertyAvailability />
          </TreatmentToggle>
          <TreatmentToggle split="property_page_availability" treatment={['off', 'control']}>
            <LegacyPropertyAvailability />
          </TreatmentToggle>
        </PageBlock>
      </Box>

      <PageBlock bg="white" py={10} narrow ref={getMenuRef('about')}>
        <div id="about-property"></div>
        <PropertyDescription property={property} />
        {hasPropertyFacilities && (
          <PropertyFacilities
            facilities={property.propertyFacilities}
            propertyName={property.name}
            interactionEventValue="Bottom Link Selected"
          />
        )}
      </PageBlock>

      <PageBlock bg="white" py={10} narrow ref={getMenuRef('location')}>
        <div id="location"></div>
        <PropertyMap property={property} />
      </PageBlock>

      <PageBlock py={10} narrow ref={getMenuRef('policies')}>
        <div id="property-policies"></div>
        <PropertyKnowBeforeYouGo
          checkInInstructions={property.checkInInstructions}
          knowBeforeYouGoDescription={property.knowBeforeYouGoDescription}
          mandatoryFeesDescription={property.mandatoryFeesDescription}
        />
      </PageBlock>

      <PageBlock bg="snow">
        <RecommendedProperties />
      </PageBlock>
    </Box>
  );
});

PropertyLayout.displayName = 'PropertyLayout';

export default PropertyLayout;
